import React, { FC, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { removeDataFromLocalStorage } from 'src/localStorage/removeDataFromLocalStorage'
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum'
import {
  LOGIN_ROUTE,
  SETTINGS_ROUTE,
  SETTINGS_SUBSCRIPTION_ROUTE,
} from 'src/constants/routes'
import { useGetSelfQuery } from 'src/reactQuery/useGetSelfQuery'
import { useGetCreditsQuery } from 'src/reactQuery/useGetCreditsQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import { useGetSubscriptionStatusQuery } from 'src/reactQuery/useGetSubscriptionStatusQuery'
import { getPlanDisplayName } from 'src/utils/getPlanDisplayName'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import { queryClient } from 'src/reactQuery'
import { SuggestionsApi } from 'src/services/suggestionsApi'
import { AnalyticsApi } from 'src/services/analyticsApi'
import { AdminApi } from 'src/services/adminApi'
import { trackLogout } from 'src/services/trackingApi'
import { AiChatApi } from 'src/services/aiChatApi'
import useOutsideClick from 'src/hooks/useOutsideClick'
import CaretIcon from 'src/assets/svg/caret.svg'
import styles from './userDropDown.module.scss'

export const UserDropDown: FC = () => {
  const navigate = useNavigate()

  const { data: userData } = useGetSelfQuery()
  const { data: creditsResponse } = useGetCreditsQuery()
  const { data: features } = useGetUserFeaturesQuery()
  const { data: subscriptionStatusData } = useGetSubscriptionStatusQuery()

  const [isDropDownOpen, setIsDropDownOpen] = useState(false)

  const dropDownRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    onOutsideClick: () => setIsDropDownOpen(false),
    ref: dropDownRef,
  })

  const user = userData?.user
  const creditsData = creditsResponse?.data
  const avatarUrl = features?.data?.avatarUrl
    ? `${ANALYTICS_API_URL}${features.data.avatarUrl}`
    : null

  // Calculate credits info
  const dailyLimit = 100 // This might come from backend later
  const usedToday = creditsData?.totalUsedToday || 0
  const usagePercentage = dailyLimit > 0 ? (usedToday / dailyLimit) * 100 : 0

  // Account info from the real subscription status.
  const subscriptionStatus = subscriptionStatusData?.data
  const currentPlan = subscriptionStatus?.plan || 'none'
  const subscriptionState = subscriptionStatus?.status
  const isSubscribed =
    subscriptionState === 'active' || subscriptionState === 'trialing'
  const accountName = getPlanDisplayName(currentPlan)

  const onLogOut = () => {
    // Before the session id is cleared: this flushes immediately, so the logout and anything still
    // buffered are attributed to the user who was signed in.
    trackLogout()

    removeDataFromLocalStorage(LocalStorageKey.SESSION_ID)
    // Layout seeds the first paint from this cache; without clearing it, the
    // next user to log in would briefly see the previous user's banner.
    removeDataFromLocalStorage(LocalStorageKey.BANNER_STATE)

    AdminApi.refresh()

    AiChatApi.refresh()

    AnalyticsApi.refresh()

    SuggestionsApi.refresh()

    queryClient.clear()

    navigate(LOGIN_ROUTE)
  }

  const onSettingsClick = () => {
    setIsDropDownOpen(false)
    navigate(SETTINGS_ROUTE)
  }

  const onActivatePlanClick = () => {
    setIsDropDownOpen(false)
    navigate(SETTINGS_SUBSCRIPTION_ROUTE)
  }

  return (
    <div className={styles.userDropDown} ref={dropDownRef}>
      <span className={styles.accountImage}>
        {avatarUrl ? (
          <img alt="Profile" src={avatarUrl} />
        ) : (
          <span>{!!user?.firstName && `${user?.firstName.charAt(0)}`}</span>
        )}
      </span>

      <button
        className={styles.dropDownButton}
        onClick={() => setIsDropDownOpen((prev) => !prev)}
      >
        {!!user?.firstName && user?.lastName && (
          <span>
            {user?.firstName}
            &nbsp;
            {user?.lastName}
          </span>
        )}

        <span
          className={classNames(
            styles.dropDownIcon,
            isDropDownOpen && styles.dropDownIconReverse
          )}
        >
          <img alt="caret" src={CaretIcon} />
        </span>
      </button>

      {isDropDownOpen && (
        <div className={styles.dropDownMenu}>
          {/* User Header Section */}
          <div className={styles.userHeader}>
            <span className={styles.userAvatar}>
              {avatarUrl ? (
                <img alt="Profile" src={avatarUrl} />
              ) : (
                <span>
                  {!!user?.firstName && `${user?.firstName.charAt(0)}`}
                </span>
              )}
            </span>
            <div className={styles.userInfo}>
              <div className={styles.userName}>
                {!!user?.firstName && user?.lastName && (
                  <>
                    {user?.firstName} {user?.lastName}
                  </>
                )}
              </div>
              <div className={styles.userEmail}>{user?.email || ''}</div>
            </div>
          </div>

          {/* Account Card Section */}
          <div
            className={classNames(
              styles.accountCard,
              isSubscribed && styles.accountCardSubscribed
            )}
          >
            <div className={styles.accountInfo}>
              <div
                className={classNames(
                  styles.accountName,
                  isSubscribed && styles.accountNameActive
                )}
              >
                {accountName}
              </div>
            </div>

            <div className={styles.creditsSection}>
              <div className={styles.creditsText}>
                AI Credits: {dailyLimit}/day | Used today: {usedToday}
              </div>
              <div className={styles.progressBarContainer}>
                <div
                  className={styles.progressBarFill}
                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                />
              </div>
            </div>

            <button
              className={styles.activateButton}
              onClick={onActivatePlanClick}
              type="button"
            >
              {isSubscribed ? 'See the Plan Details' : 'Activate Plan'}
            </button>
          </div>

          {/* Menu Items */}
          <button
            className={styles.menuItem}
            onClick={onSettingsClick}
            type="button"
          >
            <svg
              fill="none"
              height="20"
              viewBox="0 0 24 24"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                stroke="#25262D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
              <path
                d="M2 12.88V11.12C2 10.08 2.85 9.22 3.9 9.22C5.71 9.22 6.45 7.94 5.54 6.37C5.02 5.47 5.33 4.3 6.24 3.78L7.97 2.79C8.76 2.32 9.78 2.6 10.25 3.39L10.36 3.58C11.26 5.15 12.74 5.15 13.65 3.58L13.76 3.39C14.23 2.6 15.25 2.32 16.04 2.79L17.77 3.78C18.68 4.3 18.99 5.47 18.47 6.37C17.56 7.94 18.3 9.22 20.11 9.22C21.15 9.22 22.01 10.07 22.01 11.12V12.88C22.01 13.92 21.16 14.78 20.11 14.78C18.3 14.78 17.56 16.06 18.47 17.63C18.99 18.54 18.68 19.7 17.77 20.22L16.04 21.21C15.25 21.68 14.23 21.4 13.76 20.61L13.65 20.42C12.75 18.85 11.27 18.85 10.36 20.42L10.25 20.61C9.78 21.4 8.76 21.68 7.97 21.21L6.24 20.22C5.33 19.7 5.02 18.53 5.54 17.63C6.45 16.06 5.71 14.78 3.9 14.78C2.85 14.78 2 13.92 2 12.88Z"
                stroke="#25262D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
            <span>Settings</span>
          </button>

          <button className={styles.menuItem} onClick={onLogOut} type="button">
            <svg
              fill="none"
              height="20"
              viewBox="0 0 24 24"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.90002 7.56C9.21002 3.96 11.06 2.49 15.11 2.49H15.24C19.71 2.49 21.5 4.28 21.5 8.75V15.27C21.5 19.74 19.71 21.53 15.24 21.53H15.11C11.09 21.53 9.24002 20.08 8.91002 16.54"
                stroke="#25262D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
              <path
                d="M15 12H3.62"
                stroke="#25262D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
              <path
                d="M5.85 8.65L2.5 12L5.85 15.35"
                stroke="#25262D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
              />
            </svg>
            <span>Log Out</span>
          </button>
        </div>
      )}
    </div>
  )
}
